import React, { useState, useEffect } from 'react';
import { Slider } from './ui/slider';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertCircle, CheckCircle, Clock, Play, FileText, ExternalLink, ChevronDown, ChevronUp, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DailyScanForm = ({ user }) => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [commonTasks, setCommonTasks] = useState([]);
  const [sopsCount, setSopsCount] = useState(0);
  const [taskStatuses, setTaskStatuses] = useState({});
  const [onLeave, setOnLeave] = useState(false);
  const [expandedDimension, setExpandedDimension] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null); // For task popup
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    dimensions: {
      cness_tasks: { time: 0, comments: '', task_updates: [] },
      self_care: 0,
      rnd_learning: { time: 0, description: '' },
      social_media: { time: 0, description: '' },
      personal_time: 0,
      inspired_work: { time: 0, description: '' }
    },
    mood: 'Happy',
    status: 'draft',
    on_leave: false
  });
  const [totalHours, setTotalHours] = useState(0);

  useEffect(() => {
    fetchTasksAndSOPs();
  }, []);

  useEffect(() => {
    const total =
      formData.dimensions.cness_tasks.time +
      formData.dimensions.self_care +
      formData.dimensions.rnd_learning.time +
      formData.dimensions.social_media.time +
      formData.dimensions.personal_time +
      formData.dimensions.inspired_work.time;
    setTotalHours(total);
  }, [formData.dimensions]);

  // Fetch existing daily scan for today (if any)
  const fetchExistingDailyScan = async () => {
    try {
      const token = localStorage.getItem('token');
      const today = new Date().toISOString().split('T')[0];
      const response = await axios.get(`${API_URL}/daily-scans/today`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data && response.data.dimensions) {
        const dims = response.data.dimensions;
        setFormData(prev => ({
          ...prev,
          dimensions: {
            cness_tasks: dims.cness_tasks || { time: 0, comments: '', task_updates: [] },
            self_care: typeof dims.self_care === 'object' ? dims.self_care.time || 0 : dims.self_care || 0,
            rnd_learning: dims.rnd_learning || { time: 0, description: '' },
            social_media: dims.social_media || { time: 0, description: '' },
            personal_time: typeof dims.personal_time === 'object' ? dims.personal_time.time || 0 : dims.personal_time || 0,
            inspired_work: dims.inspired_work || { time: 0, description: '' }
          },
          mood: response.data.mood || 'Happy',
          on_leave: response.data.on_leave || false
        }));
        console.log('Loaded existing daily scan:', response.data);
      }
    } catch (error) {
      // No existing scan for today, that's fine
      console.log('No existing daily scan for today');
    }
  };

  useEffect(() => {
    fetchExistingDailyScan();
  }, []);

  const fetchTasksAndSOPs = async () => {
    try {
      const token = localStorage.getItem('token');
      const [tasksRes, sopsRes] = await Promise.all([
        axios.get(`${API_URL}/tasks`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/sops`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      const allTasks = tasksRes.data.tasks || tasksRes.data;
      const userTasks = allTasks.filter(t => t.assigned_to === user.id || (t.picked_by && t.picked_by.includes(user.id)));
      const unassignedTasks = allTasks.filter(t => !t.assigned_to && (!t.picked_by || !t.picked_by.includes(user.id)));
      
      setTasks(userTasks);
      setCommonTasks(unassignedTasks.slice(0, 5));
      
      const userSops = sopsRes.data.filter(s => s.assigned_to === user.id);
      setSopsCount(userSops.length);
      
      const statuses = {};
      userTasks.forEach(task => {
        statuses[task.id] = task.status || 'pending';
      });
      setTaskStatuses(statuses);
    } catch (error) {
      console.error('Failed to fetch tasks/SOPs');
    }
  };

  const handlePickTask = async (taskId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/tasks/${taskId}/assign-to-me`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Task assigned to you!');
      fetchTasksAndSOPs();
    } catch (error) {
      toast.error('Failed to assign task');
    }
  };

  const handleTaskStatusChange = (taskId, status) => {
    setTaskStatuses(prev => ({ ...prev, [taskId]: status }));
  };

  const handleDimensionChange = (field, value) => {
    if (field === 'cness_tasks' || field === 'rnd_learning' || field === 'social_media' || field === 'inspired_work') {
      setFormData({
        ...formData,
        dimensions: {
          ...formData.dimensions,
          [field]: { ...formData.dimensions[field], time: value }
        }
      });
    } else {
      setFormData({
        ...formData,
        dimensions: {
          ...formData.dimensions,
          [field]: value
        }
      });
    }
  };

  const handleSubmit = async (status) => {
    try {
      const token = localStorage.getItem('token');
      
      const task_updates = tasks.map(task => ({
        task_id: task.id,
        status: taskStatuses[task.id]
      }));
      
      const submitData = {
        date: formData.date,
        status,
        on_leave: onLeave,
        dimensions: onLeave ? null : {
          ...formData.dimensions,
          cness_tasks: {
            ...formData.dimensions.cness_tasks,
            task_updates
          }
        },
        mood: onLeave ? null : formData.mood
      };
      
      await axios.post(
        `${API_URL}/daily-scans`,
        submitData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Daily scan ${status === 'submitted' ? 'submitted' : 'saved as draft'} successfully`);
      fetchTasksAndSOPs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save daily scan');
    }
  };

  const moods = [
    { value: 'Happy', emoji: '😊', label: 'Happy', color: '#10B981' },
    { value: 'Inspired', emoji: '✨', label: 'Inspired', color: '#8B5CF6' },
    { value: 'Overworked', emoji: '😰', label: 'Overworked', color: '#F59E0B' },
    { value: 'Bored', emoji: '😑', label: 'Bored', color: '#6B7280' },
    { value: 'Low', emoji: '😔', label: 'Low', color: '#EF4444' }
  ];

  const dimensions = [
    { key: 'cness_tasks', label: 'CNESS Tasks', icon: '📋', color: '#241665', hasComments: true },
    { key: 'self_care', label: 'Self Care', icon: '🧘', color: '#10B981', hasComments: false },
    { key: 'rnd_learning', label: 'R&D', icon: '📚', color: '#6366F1', hasComments: true },
    { key: 'social_media', label: 'Creative', icon: '🎨', color: '#EC4899', hasComments: true },
    { key: 'personal_time', label: 'Personal Growth', icon: '🌱', color: '#F59E0B', hasComments: false },
    { key: 'inspired_work', label: 'Projects', icon: '📁', color: '#8B5CF6', hasComments: true }
  ];

  const getDimensionValue = (key) => {
    const dim = formData.dimensions[key];
    return typeof dim === 'object' ? dim.time : dim;
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'completed': return <CheckCircle size={16} style={{ color: '#10B981' }} />;
      case 'in_progress': case 'in-progress': return <Play size={16} style={{ color: '#F59E0B' }} />;
      default: return <Clock size={16} style={{ color: '#6B7280' }} />;
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'completed': return { bg: 'rgba(16, 185, 129, 0.1)', color: '#10B981' };
      case 'in_progress': case 'in-progress': return { bg: 'rgba(245, 158, 11, 0.1)', color: '#F59E0B' };
      default: return { bg: 'rgba(107, 114, 128, 0.1)', color: '#6B7280' };
    }
  };

  return (
    <div className="daily-scan-page" data-testid="daily-scan-form">
      {/* Header */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '24px',
        flexWrap: 'wrap',
        gap: '16px'
      }}>
        <div>
          <h2 style={{ margin: '0 0 4px 0', fontSize: '26px', fontWeight: '700', color: 'var(--text-primary)' }}>
            Daily Scan
          </h2>
          <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-tertiary)' }}>
            Track your daily activities and progress
          </p>
        </div>
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
          <input
            type="date"
            value={formData.date}
            max={new Date().toISOString().split('T')[0]}
            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
            style={{
              padding: '10px 14px',
              border: '1px solid var(--border-color)',
              borderRadius: '8px',
              fontSize: '14px',
              background: 'var(--input-bg)',
              color: 'var(--text-primary)'
            }}
            data-testid="date-selector"
          />
          <label style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            padding: '10px 16px',
            background: onLeave ? 'rgba(16, 185, 129, 0.1)' : 'var(--bg-secondary)',
            borderRadius: '8px',
            cursor: 'pointer',
            border: onLeave ? '1px solid #10B981' : '1px solid var(--border-color)'
          }}>
            <input
              type="checkbox"
              checked={onLeave}
              onChange={(e) => setOnLeave(e.target.checked)}
              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              data-testid="on-leave-checkbox"
            />
            <span style={{ fontWeight: '500', color: onLeave ? '#10B981' : 'var(--text-secondary)' }}>On Leave</span>
          </label>
        </div>
      </div>

      {onLeave ? (
        <div className="card" style={{ textAlign: 'center', padding: '60px 20px' }}>
          <h3 style={{ color: 'var(--color-primary)', fontSize: '24px', marginBottom: '16px' }}>🏖️ You're marked as On Leave</h3>
          <p style={{ color: 'var(--text-tertiary)', fontSize: '16px', marginBottom: '24px' }}>
            No need to fill the daily scan form. Just submit to record your leave.
          </p>
          <button 
            onClick={() => handleSubmit('submitted')}
            className="gradient-btn"
            style={{ padding: '12px 32px', fontSize: '16px' }}
          >
            Submit Leave Record
          </button>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
          {/* Left Column - Tasks */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {/* My Tasks Card */}
            <div className="card" style={{ padding: '20px' }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center',
                gap: '12px',
                padding: '12px 16px',
                background: 'rgba(36, 22, 101, 0.08)',
                borderRadius: '10px',
                marginBottom: '16px',
                border: '1px solid rgba(36, 22, 101, 0.15)'
              }}>
                <span style={{ fontSize: '20px' }}>📋</span>
                <div>
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: 'var(--color-primary)' }}>
                    My Tasks ({tasks.length})
                  </h3>
                  <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: 'var(--text-tertiary)' }}>
                    Update task status before submitting
                  </p>
                </div>
              </div>
              
              {tasks.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {tasks.map(task => {
                    const status = taskStatuses[task.id] || 'pending';
                    const statusStyle = getStatusColor(status);
                    return (
                      <div 
                        key={task.id} 
                        style={{
                          padding: '14px 16px',
                          background: 'var(--bg-secondary)',
                          borderRadius: '10px',
                          border: '1px solid var(--border-color)',
                          borderLeft: `4px solid ${task.priority === 'high' ? '#EF4444' : task.priority === 'medium' ? '#F59E0B' : '#6B7280'}`
                        }}
                      >
                        {/* Task Title Row */}
                        <div style={{ 
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          gap: '12px',
                          marginBottom: '10px'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1 }}>
                            <span 
                              onClick={() => setSelectedTask(task)}
                              style={{ 
                                fontSize: '15px', 
                                fontWeight: '600', 
                                color: 'var(--color-primary)',
                                lineHeight: '1.4',
                                cursor: 'pointer',
                                textDecoration: 'underline',
                                textDecorationColor: 'transparent',
                                transition: 'text-decoration-color 0.2s'
                              }}
                              onMouseEnter={(e) => e.target.style.textDecorationColor = 'var(--color-primary)'}
                              onMouseLeave={(e) => e.target.style.textDecorationColor = 'transparent'}
                              title="Click to view task details"
                            >
                              {task.title}
                            </span>
                            <button
                              onClick={() => window.open(`/dashboard/tasks/${task.id}`, '_blank')}
                              style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                color: 'var(--text-tertiary)'
                              }}
                              title="Open in new tab"
                            >
                              <ExternalLink size={14} />
                            </button>
                          </div>
                          {task.priority && (
                            <span style={{
                              padding: '3px 10px',
                              borderRadius: '6px',
                              fontSize: '11px',
                              fontWeight: '600',
                              flexShrink: 0,
                              background: task.priority === 'high' ? 'rgba(239, 68, 68, 0.1)' : 
                                         task.priority === 'medium' ? 'rgba(245, 158, 11, 0.1)' : 'rgba(107, 114, 128, 0.1)',
                              color: task.priority === 'high' ? '#EF4444' : 
                                     task.priority === 'medium' ? '#F59E0B' : '#6B7280',
                              textTransform: 'capitalize'
                            }}>
                              {task.priority}
                            </span>
                          )}
                        </div>
                        
                        {/* Status Dropdown Row */}
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between'
                        }}>
                          <span style={{ 
                            fontSize: '12px', 
                            color: 'var(--text-tertiary)',
                            fontWeight: '500'
                          }}>
                            Update Status:
                          </span>
                          <select
                            value={status}
                            onChange={(e) => handleTaskStatusChange(task.id, e.target.value)}
                            style={{
                              padding: '8px 12px',
                              borderRadius: '8px',
                              border: '1px solid var(--border-color)',
                              background: statusStyle.bg,
                              color: statusStyle.color,
                              fontWeight: '600',
                              fontSize: '13px',
                              cursor: 'pointer',
                              minWidth: '130px'
                            }}
                          >
                            <option value="pending">⏳ Pending</option>
                            <option value="in_progress">🔄 In Progress</option>
                            <option value="completed">✅ Completed</option>
                          </select>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '30px 20px', 
                  color: 'var(--text-muted)',
                  background: 'var(--bg-secondary)',
                  borderRadius: '10px'
                }}>
                  <p style={{ margin: 0, fontSize: '14px' }}>No tasks assigned to you</p>
                  <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: 'var(--text-tertiary)' }}>
                    Pick a task from Available Tasks below
                  </p>
                </div>
              )}
            </div>

            {/* Common Tasks Card */}
            {commonTasks.length > 0 && (
              <div className="card" style={{ padding: '20px' }}>
                <div style={{ 
                  padding: '10px 14px',
                  background: '#E7EDF9',
                  borderRadius: '8px',
                  marginBottom: '16px'
                }}>
                  <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '600', color: 'var(--text-primary)' }}>
                    🌟 Available Tasks
                  </h3>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {commonTasks.map(task => (
                    <div 
                      key={task.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '12px',
                        background: 'var(--bg-secondary)',
                        borderRadius: '10px',
                        border: '1px solid var(--border-color)'
                      }}
                    >
                      <div style={{ fontSize: '14px', fontWeight: '500', color: 'var(--text-primary)' }}>
                        {task.title}
                      </div>
                      <button
                        onClick={() => handlePickTask(task.id)}
                        className="gradient-btn"
                        style={{ padding: '6px 12px', fontSize: '12px' }}
                      >
                        Pick
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* View SOPs Link */}
            <div 
              className="card" 
              style={{ 
                padding: '16px 20px', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onClick={() => navigate('/dashboard/sops')}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  background: 'rgba(99, 102, 241, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <FileText size={20} style={{ color: '#6366F1' }} />
                </div>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)' }}>
                    View My SOPs
                  </div>
                  <div style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>
                    {sopsCount} SOP{sopsCount !== 1 ? 's' : ''} assigned
                  </div>
                </div>
              </div>
              <ExternalLink size={18} style={{ color: 'var(--text-muted)' }} />
            </div>
          </div>

          {/* Right Column - Dimensions & Mood */}
          <div className="card" style={{ padding: '20px' }}>
            <h3 style={{ margin: '0 0 20px 0', fontSize: '18px', fontWeight: '600', color: 'var(--text-primary)' }}>
              Time Tracking
            </h3>

            {/* Total Hours Summary */}
            <div style={{
              padding: '16px',
              background: totalHours > 24 ? 'rgba(239, 68, 68, 0.1)' : '#E7EDF9',
              borderRadius: '10px',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <span style={{ fontSize: '14px', fontWeight: '500', color: 'var(--text-secondary)' }}>
                Total Time Today
              </span>
              <span style={{ 
                fontSize: '22px', 
                fontWeight: '700', 
                color: totalHours > 24 ? '#EF4444' : 'var(--color-primary)',
                fontFamily: 'monospace'
              }}>
                {String(Math.floor(totalHours)).padStart(2, '0')}:{String(Math.floor((totalHours * 60) % 60)).padStart(2, '0')}:{String(Math.floor((totalHours * 3600) % 60)).padStart(2, '0')}
              </span>
            </div>

            {totalHours > 24 && (
              <div style={{
                padding: '12px',
                background: 'rgba(239, 68, 68, 0.1)',
                borderRadius: '8px',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                color: '#EF4444',
                fontSize: '13px'
              }}>
                <AlertCircle size={18} />
                Total hours exceed 24. Please review your entries.
              </div>
            )}

            {/* Dimension Cards */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {dimensions.map((dim) => {
                const value = getDimensionValue(dim.key);
                const isExpanded = expandedDimension === dim.key;
                
                return (
                  <div key={dim.key} style={{
                    border: '1px solid var(--border-color)',
                    borderRadius: '10px',
                    overflow: 'hidden'
                  }}>
                    <div 
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '12px 16px',
                        cursor: dim.hasComments ? 'pointer' : 'default',
                        background: 'var(--bg-surface)'
                      }}
                      onClick={() => dim.hasComments && setExpandedDimension(isExpanded ? null : dim.key)}
                    >
                      <span style={{ fontSize: '18px', marginRight: '10px' }}>{dim.icon}</span>
                      <span style={{ flex: 1, fontSize: '14px', fontWeight: '500', color: 'var(--text-primary)' }}>
                        {dim.label}
                      </span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                          <input
                            type="number"
                            value={Math.floor(value) || 0}
                            onChange={(e) => {
                              const hours = parseInt(e.target.value) || 0;
                              const totalSeconds = value * 3600;
                              const mins = Math.floor((totalSeconds % 3600) / 60);
                              const secs = Math.floor(totalSeconds % 60);
                              handleDimensionChange(dim.key, hours + (mins / 60) + (secs / 3600));
                            }}
                            onClick={(e) => e.stopPropagation()}
                            min="0"
                            max="24"
                            style={{
                              width: '42px',
                              padding: '6px 4px',
                              border: '1px solid var(--border-color)',
                              borderRadius: '6px',
                              textAlign: 'center',
                              fontSize: '13px',
                              fontWeight: '600',
                              color: dim.color,
                              background: 'var(--input-bg)'
                            }}
                          />
                          <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>h</span>
                        </div>
                        <span style={{ color: 'var(--text-muted)', fontWeight: '600' }}>:</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                          <input
                            type="number"
                            value={Math.floor((value * 60) % 60) || 0}
                            onChange={(e) => {
                              const hours = Math.floor(value);
                              const mins = Math.min(59, Math.max(0, parseInt(e.target.value) || 0));
                              const totalSeconds = value * 3600;
                              const secs = Math.floor(totalSeconds % 60);
                              handleDimensionChange(dim.key, hours + (mins / 60) + (secs / 3600));
                            }}
                            onClick={(e) => e.stopPropagation()}
                            min="0"
                            max="59"
                            style={{
                              width: '42px',
                              padding: '6px 4px',
                              border: '1px solid var(--border-color)',
                              borderRadius: '6px',
                              textAlign: 'center',
                              fontSize: '13px',
                              fontWeight: '600',
                              color: dim.color,
                              background: 'var(--input-bg)'
                            }}
                          />
                          <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>m</span>
                        </div>
                        <span style={{ color: 'var(--text-muted)', fontWeight: '600' }}>:</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                          <input
                            type="number"
                            value={Math.floor((value * 3600) % 60) || 0}
                            onChange={(e) => {
                              const hours = Math.floor(value);
                              const mins = Math.floor((value * 60) % 60);
                              const secs = Math.min(59, Math.max(0, parseInt(e.target.value) || 0));
                              handleDimensionChange(dim.key, hours + (mins / 60) + (secs / 3600));
                            }}
                            onClick={(e) => e.stopPropagation()}
                            min="0"
                            max="59"
                            style={{
                              width: '42px',
                              padding: '6px 4px',
                              border: '1px solid var(--border-color)',
                              borderRadius: '6px',
                              textAlign: 'center',
                              fontSize: '13px',
                              fontWeight: '600',
                              color: dim.color,
                              background: 'var(--input-bg)'
                            }}
                          />
                          <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>s</span>
                        </div>
                        {dim.hasComments && (
                          isExpanded ? <ChevronUp size={18} style={{ color: 'var(--text-muted)', marginLeft: '4px' }} /> 
                                     : <ChevronDown size={18} style={{ color: 'var(--text-muted)', marginLeft: '4px' }} />
                        )}
                      </div>
                    </div>
                    
                    {/* Expanded Comments Section */}
                    {dim.hasComments && isExpanded && (
                      <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border-color)', background: 'var(--bg-secondary)' }}>
                        <textarea
                          placeholder={`Add notes for ${dim.label.toLowerCase()}...`}
                          value={typeof formData.dimensions[dim.key] === 'object' ? 
                            (formData.dimensions[dim.key].comments || formData.dimensions[dim.key].description || '') : ''}
                          onChange={(e) => {
                            const field = dim.key === 'cness_tasks' ? 'comments' : 'description';
                            setFormData({
                              ...formData,
                              dimensions: {
                                ...formData.dimensions,
                                [dim.key]: { ...formData.dimensions[dim.key], [field]: e.target.value }
                              }
                            });
                          }}
                          style={{
                            width: '100%',
                            padding: '10px',
                            border: '1px solid var(--border-color)',
                            borderRadius: '6px',
                            minHeight: '80px',
                            resize: 'vertical',
                            fontSize: '13px',
                            background: 'var(--input-bg)',
                            color: 'var(--text-primary)'
                          }}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Mood Selection */}
            <div style={{ marginTop: '24px' }}>
              <h4 style={{ margin: '0 0 12px 0', fontSize: '15px', fontWeight: '600', color: 'var(--text-primary)' }}>
                How are you feeling?
              </h4>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                {moods.map((mood) => (
                  <button
                    key={mood.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, mood: mood.value })}
                    style={{
                      flex: '1',
                      minWidth: '80px',
                      padding: '12px 8px',
                      background: formData.mood === mood.value ? `${mood.color}15` : 'var(--bg-secondary)',
                      border: formData.mood === mood.value ? `2px solid ${mood.color}` : '1px solid var(--border-color)',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '6px',
                      transition: 'all 0.2s'
                    }}
                  >
                    <span style={{ fontSize: '24px' }}>{mood.emoji}</span>
                    <span style={{ 
                      fontSize: '11px', 
                      fontWeight: '600', 
                      color: formData.mood === mood.value ? mood.color : 'var(--text-secondary)'
                    }}>
                      {mood.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Submit Buttons */}
            <div style={{ 
              display: 'flex', 
              gap: '12px', 
              marginTop: '24px',
              paddingTop: '20px',
              borderTop: '1px solid var(--border-color)'
            }}>
              <button
                type="button"
                className="btn-secondary"
                onClick={() => handleSubmit('draft')}
                style={{ flex: 1, padding: '12px' }}
              >
                Save Draft
              </button>
              <button
                type="button"
                className="gradient-btn"
                onClick={() => handleSubmit('submitted')}
                style={{ flex: 1, padding: '12px' }}
              >
                Submit Scan
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Task Detail Popup Modal */}
      {selectedTask && (
        <div 
          className="modal-overlay" 
          onClick={() => setSelectedTask(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
        >
          <div 
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--bg-primary)',
              borderRadius: '16px',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '80vh',
              overflow: 'auto',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
            }}
          >
            {/* Modal Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '20px 24px',
              borderBottom: '1px solid var(--border-color)',
              background: 'var(--bg-secondary)'
            }}>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: 'var(--text-primary)' }}>
                Task Details
              </h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button
                  onClick={() => window.open(`/dashboard/tasks/${selectedTask.id}`, '_blank')}
                  style={{
                    background: 'var(--bg-primary)',
                    border: '1px solid var(--border-color)',
                    borderRadius: '8px',
                    padding: '8px 12px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '13px',
                    color: 'var(--text-secondary)'
                  }}
                  title="Open in new tab"
                >
                  <ExternalLink size={14} />
                  Open Full Page
                </button>
                <button
                  onClick={() => setSelectedTask(null)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '8px',
                    display: 'flex',
                    color: 'var(--text-tertiary)'
                  }}
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '24px' }}>
              {/* Task Title */}
              <h2 style={{ 
                margin: '0 0 16px 0', 
                fontSize: '22px', 
                fontWeight: '700', 
                color: 'var(--text-primary)',
                lineHeight: '1.3'
              }}>
                {selectedTask.title}
              </h2>

              {/* Meta Info */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '20px' }}>
                {selectedTask.priority && (
                  <span style={{
                    padding: '6px 12px',
                    borderRadius: '8px',
                    fontSize: '12px',
                    fontWeight: '600',
                    background: selectedTask.priority === 'high' ? 'rgba(239, 68, 68, 0.1)' : 
                               selectedTask.priority === 'medium' ? 'rgba(245, 158, 11, 0.1)' : 'rgba(107, 114, 128, 0.1)',
                    color: selectedTask.priority === 'high' ? '#EF4444' : 
                           selectedTask.priority === 'medium' ? '#F59E0B' : '#6B7280',
                    textTransform: 'capitalize'
                  }}>
                    {selectedTask.priority} Priority
                  </span>
                )}
                <span style={{
                  padding: '6px 12px',
                  borderRadius: '8px',
                  fontSize: '12px',
                  fontWeight: '600',
                  background: getStatusColor(taskStatuses[selectedTask.id] || selectedTask.status || 'pending').bg,
                  color: getStatusColor(taskStatuses[selectedTask.id] || selectedTask.status || 'pending').color
                }}>
                  {(taskStatuses[selectedTask.id] || selectedTask.status || 'pending').replace('_', ' ')}
                </span>
                {selectedTask.deadline && (
                  <span style={{
                    padding: '6px 12px',
                    borderRadius: '8px',
                    fontSize: '12px',
                    fontWeight: '500',
                    background: 'rgba(59, 130, 246, 0.1)',
                    color: '#3B82F6',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    <Clock size={12} />
                    Due: {new Date(selectedTask.deadline).toLocaleDateString()}
                  </span>
                )}
              </div>

              {/* Description */}
              <div style={{ marginBottom: '24px' }}>
                <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: '600', color: 'var(--text-secondary)' }}>
                  Description
                </h4>
                <p style={{ 
                  margin: 0, 
                  fontSize: '14px', 
                  color: 'var(--text-primary)', 
                  lineHeight: '1.6',
                  padding: '12px 16px',
                  background: 'var(--bg-secondary)',
                  borderRadius: '8px'
                }}>
                  {selectedTask.description || 'No description provided'}
                </p>
              </div>

              {/* Update Status */}
              <div style={{
                padding: '16px',
                background: 'var(--bg-secondary)',
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <span style={{ fontSize: '14px', fontWeight: '500', color: 'var(--text-primary)' }}>
                  Update Status:
                </span>
                <select
                  value={taskStatuses[selectedTask.id] || 'pending'}
                  onChange={(e) => handleTaskStatusChange(selectedTask.id, e.target.value)}
                  style={{
                    padding: '10px 16px',
                    borderRadius: '8px',
                    border: '1px solid var(--border-color)',
                    background: getStatusColor(taskStatuses[selectedTask.id] || 'pending').bg,
                    color: getStatusColor(taskStatuses[selectedTask.id] || 'pending').color,
                    fontWeight: '600',
                    fontSize: '14px',
                    cursor: 'pointer',
                    minWidth: '150px'
                  }}
                >
                  <option value="pending">⏳ Pending</option>
                  <option value="in_progress">🔄 In Progress</option>
                  <option value="completed">✅ Completed</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DailyScanForm;
